import React, { useCallback, useRef, useState } from "react";
import { Box, IconButton, MenuItem, Select, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { MessageDialog } from "../message-dialog";
import { unityOptions } from "../../models/unity-options.model";

type ReviewItemProps = {
    id: number;
    name: string;
    min: string;
    max: string;
    unity?: string;
    volume?: string;
    description: string;
    onItemClick: (id: number) => void;
    onRemoveItem?: (id: number) => void;
    onItemChange: (id: number, volume: string, unity: string) => void;
};

export const OralItem: React.FC<ReviewItemProps> = ({ id, name, volume, min, max, unity, onItemClick, onRemoveItem, onItemChange }) => {
    const theme = useTheme();
    const inputRef = useRef("");
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [unityOral, setUnityOral] = useState<string>(unity ? unity : "mg/dia");
    const [inputValue, setInputValue] = useState<string>(volume ? volume + unityOral : "");

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, setter: Function) => {
        let newString = event.target.value
            .replace(/[^\d,.]/g, "")
            .replace(",", ".")
            .replace(/\.(?=.*\.)+/g, "");

        const [integerPart, decimalPart] = newString.split(".");
        if (integerPart.length > 5) {
            newString = integerPart.slice(0, 5) + (decimalPart ? `.${decimalPart}` : "");
        }
        setter(newString);
    }, []);

    const handleOnBlur = (onBlurEvent: React.FocusEvent<HTMLInputElement>) => {
        if (onBlurEvent.target.value === "") {
            setInputValue("");
            return;
        }
        setInputValue(onBlurEvent.target.value + `${unityOral}`);
    };

    const handleChange = (value: string, nextUnity?: string) => {
        onItemChange(id, value, nextUnity || unityOral);
    };

    const handleUnity = useCallback(
        (event: { target: { value: string } }) => {
            const newUnity = event.target.value;
            setUnityOral(newUnity);
            setInputValue((prev) => {
                const updatedValue = prev.replace(new RegExp(unityOral + "$"), "");
                handleChange(updatedValue, newUnity);
                return updatedValue + newUnity;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [unityOral, id]
    );

    const handleDeleteItem = () => {
        onRemoveItem?.(id);
        setShowDeleteDialog(false);
    };

    const dosageLabel = (label: string, value: string) => (
        <Box sx={{ textAlign: "center", flexShrink: 0 }}>
            <Typography sx={{ fontSize: "11px", color: "text.secondary", lineHeight: 1.2 }}>{label}</Typography>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "text.primary" }}>
                {value}
                {unity}
            </Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                p: "14px 16px",
                borderRadius: "13px",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: "background.paper",
            }}
        >
            <MessageDialog
                open={showDeleteDialog}
                message={"Tem certeza que deseja remover o Ativo?"}
                onClose={() => setShowDeleteDialog(false)}
                onConfirm={handleDeleteItem}
                confirmButtonText={"Deletar"}
                cancelButtonText={"Cancelar"}
            />

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography noWrap sx={{ fontSize: "15px", fontWeight: 600, color: "text.primary", minWidth: 0 }}>
                    {name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                    {onRemoveItem && (
                        <Tooltip title="Remover o ativo" placement="bottom">
                            <IconButton size="small" onClick={() => setShowDeleteDialog(true)} sx={{ color: "text.secondary" }}>
                                <DeleteOutlineOutlinedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Informações" placement="bottom">
                        <IconButton size="small" onClick={() => onItemClick(id)} sx={{ color: "text.secondary" }}>
                            <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, flexWrap: "wrap" }}>
                {dosageLabel("Min", min)}
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <TextField
                        key={id}
                        color="primary"
                        value={inputValue}
                        placeholder="0"
                        onChange={(e) => {
                            handleInputChange(e, setInputValue);
                            handleChange(e.target.value);
                        }}
                        onBlur={handleOnBlur}
                        size="small"
                        inputProps={{ inputMode: "decimal", "aria-label": `Volume de ${name}` }}
                        sx={{
                            width: 88,
                            "& .MuiOutlinedInput-root": { height: 46, backgroundColor: theme.palette.brand.surface2 },
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.divider, borderWidth: "1.5px" },
                            "& .MuiOutlinedInput-input": {
                                textAlign: "center",
                                color: "primary.main",
                                fontWeight: 600,
                                fontSize: "15px",
                                "&::placeholder": { color: "text.disabled", opacity: 1, fontWeight: 500 },
                            },
                        }}
                        InputProps={{
                            onFocus: (e) => setInputValue(e.target.value.replace(new RegExp(unityOral, "g"), "")),
                            ref: inputRef,
                        }}
                    />
                    <Select value={unityOral} onChange={handleUnity} size="small" inputProps={{ "aria-label": `Unidade de ${name}` }} sx={{ height: 46, minWidth: 72 }}>
                        {unityOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                {dosageLabel("Max", max)}
            </Box>
        </Box>
    );
};
